import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

/**
 * Marketing push campaigns (API: /api/push-campaigns, worker-delivered through FCM).
 *
 * Conventions, same as brandApi/membershipApi: reads return data or a safe default and toast
 * the error; mutations return the record (or null) and let the caller toast success. Every
 * call is merchant-scoped — a platform admin must pass merchantId, a merchant user's is
 * inferred server-side from their account.
 *
 * Multipart: the campaign image travels as `image1` with `dataType=campaign` appended FIRST
 * (multer reads it to pick the upload folder before it sees the file). `audience` and
 * `deepLink` are JSON-stringified; the API parses them back.
 */

const EMPTY_LIST = { items: [], total: 0, page: 1, limit: 25, totalPages: 0 };

const readFail = (error, fallback, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return fallback;
};

const writeFail = (error, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return null;
};

/**
 * Build the multipart body for create/update.
 * @param {object} fields  { merchantId?, appId?, name, titleEn, bodyEn, titleAr?, bodyAr?, audience, deepLink?, sendMode, scheduledAt?, removeImage? }
 * @param {File|null} imageFile
 */
export const toCampaignFormData = (fields, imageFile) => {
  const fd = new FormData();
  fd.append("dataType", "campaign"); // must precede the file
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key === "audience" || key === "deepLink") {
      fd.append(key, value === null ? "" : JSON.stringify(value));
      return;
    }
    fd.append(key, value === null ? "" : String(value));
  });
  if (imageFile instanceof File) fd.append("image1", imageFile);
  return fd;
};

// GET /api/push-campaigns/apps?merchantId → { linked, apps, errorCode?, message? }
export const getCampaignApps = async (merchantId) => {
  const empty = { linked: false, apps: [] };
  try {
    const res = await axiosInstance.get("/api/push-campaigns/apps", { params: { merchantId } });
    if (res?.data && !res.data.isError) return res.data.data || empty;
    errorToast(res?.data?.message ?? "Error loading target apps");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading target apps");
  }
};

// GET /api/push-campaigns/limits?merchantId → { weeklyLimit, usedThisWeek, remaining, nextAllowedAt }
export const getCampaignLimits = async (merchantId) => {
  try {
    const res = await axiosInstance.get("/api/push-campaigns/limits", { params: { merchantId } });
    if (res?.data && !res.data.isError) return res.data.data || null;
    return null;
  } catch (error) {
    console.error("Error loading campaign limits:", error);
    return null;
  }
};

// POST /api/push-campaigns/estimate → { users, tokens, byPlatform:{ios,android,unknown}, apps }
// Returns { error: { code, message } } on a 400 so the composer can show it inline (no toast).
export const estimateCampaignReach = async ({ merchantId, appId, audience }) => {
  try {
    const res = await axiosInstance.post("/api/push-campaigns/estimate", { merchantId, appId: appId || null, audience });
    if (res?.data && !res.data.isError) return res.data.data;
    return { error: { code: res?.data?.errorCode, message: res?.data?.message } };
  } catch (error) {
    const data = error?.response?.data;
    return { error: { code: data?.errorCode, message: data?.message ?? error?.message ?? "Could not estimate reach" } };
  }
};

// GET /api/push-campaigns?merchantId&status&search&page&limit
export const listCampaigns = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/push-campaigns", { params });
    if (res?.data && !res.data.isError) return res.data.data || EMPTY_LIST;
    errorToast(res?.data?.message ?? "Error loading campaigns");
    return EMPTY_LIST;
  } catch (error) {
    return readFail(error, EMPTY_LIST, "Error loading campaigns");
  }
};

// GET /api/push-campaigns/:id
export const getCampaign = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/push-campaigns/${id}`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading campaign");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading campaign");
  }
};

/**
 * POST /api/push-campaigns (multipart). A 429 (weekly cap) comes back as
 * { capBlocked: { weeklyLimit, usedThisWeek, nextAllowedAt }, message } instead of null so the
 * composer can explain it in place.
 */
export const createCampaign = async (fields, imageFile) => {
  try {
    const res = await axiosInstance.post("/api/push-campaigns", toCampaignFormData(fields, imageFile), { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving campaign");
    return null;
  } catch (error) {
    if (error?.response?.status === 429) {
      return { capBlocked: error.response.data?.data || {}, message: error.response.data?.message };
    }
    return writeFail(error, "Error saving campaign");
  }
};

// PUT /api/push-campaigns/:id (multipart) — draft/scheduled only. Same 429 shape as create.
export const updateCampaign = async (id, fields, imageFile) => {
  try {
    const res = await axiosInstance.put(`/api/push-campaigns/${id}`, toCampaignFormData(fields, imageFile), { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving campaign");
    return null;
  } catch (error) {
    if (error?.response?.status === 429) {
      return { capBlocked: error.response.data?.data || {}, message: error.response.data?.message };
    }
    return writeFail(error, "Error saving campaign");
  }
};

// POST /api/push-campaigns/:id/cancel
export const cancelCampaign = async (id) => {
  try {
    const res = await axiosInstance.post(`/api/push-campaigns/${id}/cancel`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error cancelling campaign");
    return null;
  } catch (error) {
    return writeFail(error, "Error cancelling campaign");
  }
};

// POST /api/push-campaigns/:id/duplicate → the new draft
export const duplicateCampaign = async (id) => {
  try {
    const res = await axiosInstance.post(`/api/push-campaigns/${id}/duplicate`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error duplicating campaign");
    return null;
  } catch (error) {
    return writeFail(error, "Error duplicating campaign");
  }
};

// POST /api/push-campaigns/:id/test { phoneNo? } → { tokens, sent, failed, pruned }
export const sendTestCampaign = async (id, phoneNo) => {
  try {
    const res = await axiosInstance.post(`/api/push-campaigns/${id}/test`, { phoneNo: phoneNo || undefined });
    if (res?.data && !res.data.isError) return { ...res.data.data, message: res.data.message };
    errorToast(res?.data?.message ?? "Error sending test");
    return null;
  } catch (error) {
    return writeFail(error, "Error sending test");
  }
};
